import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RetryableError } from '../../../shared/RetryableError';
import { ConfirmDialog } from '../../job-roles/components/ConfirmDialog';
import { ErrorSummary, type SummaryIssue } from '../../job-roles/components/ErrorSummary';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { ExportErrorCode, exportErrorMessage, hasCode, isForbidden } from '../exportErrors';
import { formatDate } from '../exportFormat';
import type { EmployeeCandidate, EmployeeGroup, EmployeeGroupMember } from '../exportTypes';
import { accountingGateway } from '../index';
import { useExportCapabilities } from '../useExportCapabilities';
import '../../job-roles/job-roles.css';
import '../accounting-export.css';

type GroupsState = { kind: 'loading' } | { kind: 'ready'; groups: EmployeeGroup[] } | { kind: 'error'; message: string };
type MembersState = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; members: EmployeeGroupMember[] } | { kind: 'forbidden' } | { kind: 'error'; message: string };

const todayIso = () => new Date().toISOString().slice(0, 10);
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Create or edit a group. A save carries the version it was based on, so two people editing at once never overwrite each other. */
function GroupDialog({ group, onClose, onSaved }: { group: EmployeeGroup | null; onClose: () => void; onSaved: (g: EmployeeGroup) => void }) {
  const uid = useId();
  const summaryRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [date, setDate] = useState(group ? (group.effectiveTo ?? '') : todayIso());
  const [issues, setIssues] = useState<SummaryIssue[]>([]);
  const [serverError, setServerError] = useState<{ message: string; conflict: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const idOf = (k: string) => `${uid}-${k}`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    const found: SummaryIssue[] = [];
    if (!name.trim()) found.push({ key: 'name', message: 'Enter a group name.' });
    if (name.trim().length > 100) found.push({ key: 'name', message: 'Keep the group name to 100 characters or fewer.' });
    if (!group && !DATE_PATTERN.test(date)) found.push({ key: 'date', message: 'Enter the date the group takes effect.' });
    if (group && date && group.effectiveFrom && date < group.effectiveFrom) found.push({ key: 'date', message: 'The end date cannot be before the start date.' });
    setIssues(found);
    if (found.length > 0) {
      queueMicrotask(() => summaryRef.current?.focus());
      return;
    }
    setBusy(true);
    try {
      const saved = group
        ? await accountingGateway.updateEmployeeGroup(group.id, { name, description, effectiveTo: date || null, expectedVersion: group.concurrencyVersion })
        : await accountingGateway.createEmployeeGroup({ name, description, effectiveFrom: date });
      onSaved(saved);
    } catch (err) {
      setServerError({ message: exportErrorMessage(err, 'The group could not be saved.'), conflict: hasCode(err, ExportErrorCode.VersionConflict) });
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title={group ? 'Edit employee group' : 'New employee group'} busy={busy} onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form={idOf('form')} className="jr-btn jr-btn--primary" disabled={busy}>{busy ? 'Saving…' : 'Save group'}</button>
        </>
      )}
    >
      <form id={idOf('form')} onSubmit={(e) => void submit(e)} noValidate className="ax-form">
        <p className="jr-hint">A group is a set of employees your company defines to map wages to an account. It is never deleted: deactivate it when it is no longer used.</p>
        <ErrorSummary ref={summaryRef} issues={issues} onSelect={(key) => document.getElementById(idOf(key))?.focus()} />
        {serverError && (
          <p className="jr-error jr-error--block" role="alert">
            {serverError.message}{serverError.conflict && <> Close this window and reopen the group to see the latest version.</>}
          </p>
        )}
        <div className="jr-field">
          <label htmlFor={idOf('name')}>Group name <span aria-hidden="true" className="jr-req">*</span></label>
          <input id={idOf('name')} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required data-autofocus data-testid="group-name" />
        </div>
        <div className="jr-field">
          <label htmlFor={idOf('description')}>Description (optional)</label>
          <input id={idOf('description')} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} data-testid="group-description" />
        </div>
        <div className="jr-field">
          <label htmlFor={idOf('date')}>{group ? 'Effective to (optional)' : 'Effective from'} {!group && <span aria-hidden="true" className="jr-req">*</span>}</label>
          <input id={idOf('date')} type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="group-date" />
          <p className="jr-hint">{group ? 'Leave empty for a group with no end date.' : 'A group applies to a payroll whose accounting date is on or after this date.'}</p>
        </div>
      </form>
    </ModalDialog>
  );
}

/** Add employees to a group. The list shows employee numbers first; the server checks every employee belongs to this company. */
function AddMembersDialog({ group, onClose, onSaved }: { group: EmployeeGroup; onClose: () => void; onSaved: (count: number) => void }) {
  const uid = useId();
  const [candidates, setCandidates] = useState<EmployeeCandidate[] | 'loading' | { error: string }>('loading');
  const [filter, setFilter] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const idOf = (k: string) => `${uid}-${k}`;

  useEffect(() => {
    const controller = new AbortController();
    accountingGateway.listEmployeeCandidates(undefined, controller.signal)
      .then((list) => { if (!controller.signal.aborted) setCandidates(list); })
      .catch((err) => { if (!controller.signal.aborted) setCandidates({ error: isForbidden(err) ? 'Adding members needs permission to view employees, which you do not have.' : exportErrorMessage(err, 'The employee list could not be loaded.') }); });
    return () => controller.abort();
  }, []);

  const shown = useMemo(() => {
    if (!Array.isArray(candidates)) return [];
    const term = filter.trim().toLowerCase();
    return candidates.filter((c) => !term || c.employeeNumber.toLowerCase().includes(term) || c.name.toLowerCase().includes(term));
  }, [candidates, filter]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (chosen.size === 0) return setError('Choose at least one employee.');
    if (!DATE_PATTERN.test(from)) return setError('Enter the date the membership starts.');
    if (to && to < from) return setError('The end date cannot be before the start date.');
    setBusy(true);
    try {
      await accountingGateway.addEmployeeGroupMembers(group.id, { employeeIds: [...chosen], effectiveFrom: from, ...(to ? { effectiveTo: to } : {}) });
      onSaved(chosen.size);
    } catch (err) {
      setError(exportErrorMessage(err, 'The members could not be added.'));
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title={`Add members to ${group.name}`} size="lg" busy={busy} onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form={idOf('form')} className="jr-btn jr-btn--primary" disabled={busy || chosen.size === 0} data-testid="add-members-submit">
            {busy ? 'Adding…' : chosen.size === 0 ? 'Add members' : chosen.size === 1 ? 'Add 1 member' : `Add ${chosen.size} members`}
          </button>
        </>
      )}
    >
      <form id={idOf('form')} onSubmit={(e) => void submit(e)} noValidate className="ax-form">
        <p className="jr-hint">Membership is evaluated on the accounting date of a payroll. An employee in two active groups on that date blocks a payroll that is mapped by employee group.</p>
        {error && <p className="jr-error jr-error--block" role="alert">{error}</p>}
        {candidates === 'loading' && <p role="status" className="muted">Loading employees…</p>}
        {!Array.isArray(candidates) && candidates !== 'loading' && <p className="jr-readonly" role="note">{candidates.error}</p>}
        {Array.isArray(candidates) && (
          <>
            <div className="jr-field">
              <label htmlFor={idOf('filter')}>Find an employee</label>
              <input id={idOf('filter')} value={filter} onChange={(e) => setFilter(e.target.value)} data-autofocus placeholder="Employee number or name" />
            </div>
            <fieldset className="jr-pick" data-testid="member-candidates">
              <legend>Employees ({shown.length})</legend>
              {shown.length === 0 && <p className="muted">No employee matches.</p>}
              {shown.map((c) => (
                <label key={c.id} className="jr-pick__row">
                  <input type="checkbox" checked={chosen.has(c.id)} onChange={(e) => setChosen((prev) => { const next = new Set(prev); if (e.target.checked) next.add(c.id); else next.delete(c.id); return next; })} />
                  <span><strong>{c.employeeNumber}</strong> {c.name}</span>
                </label>
              ))}
            </fieldset>
            <div className="jr-grid">
              <div className="jr-field">
                <label htmlFor={idOf('from')}>Effective from <span aria-hidden="true" className="jr-req">*</span></label>
                <input id={idOf('from')} type="date" value={from} onChange={(e) => setFrom(e.target.value)} required data-testid="member-from" />
              </div>
              <div className="jr-field">
                <label htmlFor={idOf('to')}>Effective to (optional)</label>
                <input id={idOf('to')} type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="member-to" />
              </div>
            </div>
          </>
        )}
      </form>
    </ModalDialog>
  );
}

function EndMembershipDialog({ member, onClose, onDone }: { member: EmployeeGroupMember; onClose: () => void; onDone: () => void }) {
  const uid = useId();
  const [date, setDate] = useState(todayIso() < member.effectiveFrom ? member.effectiveFrom : todayIso());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!DATE_PATTERN.test(date) || date < member.effectiveFrom) return setError('Enter an end date on or after the day the membership started.');
    setBusy(true);
    try {
      await accountingGateway.endEmployeeGroupMembership(member.membershipId, date);
      onDone();
    } catch (err) {
      setError(exportErrorMessage(err, 'The membership could not be ended.'));
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title="End membership" size="sm" busy={busy} onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form={`${uid}-form`} className="jr-btn jr-btn--primary" disabled={busy}>{busy ? 'Ending…' : 'End membership'}</button>
        </>
      )}
    >
      <form id={`${uid}-form`} onSubmit={(e) => void submit(e)} noValidate>
        <p>Employee <strong>{member.employeeReference}</strong> stays a member up to and including the end date. The membership is kept as history; payrolls dated after it no longer count the employee in this group.</p>
        {error && <p className="jr-error jr-error--block" role="alert">{error}</p>}
        <div className="jr-field">
          <label htmlFor={`${uid}-date`}>Last day in the group</label>
          <input id={`${uid}-date`} type="date" value={date} min={member.effectiveFrom} onChange={(e) => setDate(e.target.value)} data-autofocus data-testid="end-membership-date" />
        </div>
      </form>
    </ModalDialog>
  );
}

/**
 * Company-defined employee groups: the "Employee group" dimension of By group wage mapping. Groups are created, edited and deactivated (never deleted); members are added
 * and ended with effective dates and are shown by employee number, never by name. Everything here is for mapping wages to accounts: it does not change anyone's pay.
 */
export function EmployeeGroupsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { caps } = useExportCapabilities();
  const canManage = caps?.canManageMappings !== false;
  const selectedId = searchParams.get('groupId') ?? '';
  const [state, setState] = useState<GroupsState>({ kind: 'loading' });
  const [keyedMembers, setKeyedMembers] = useState<{ key: string; state: MembersState }>({ key: '', state: { kind: 'idle' } });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [dialog, setDialog] = useState<{ kind: 'edit'; group: EmployeeGroup | null } | { kind: 'add'; group: EmployeeGroup } | { kind: 'end'; member: EmployeeGroupMember } | { kind: 'deactivate'; group: EmployeeGroup } | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    accountingGateway.listEmployeeGroups(controller.signal)
      .then((groups) => { if (!controller.signal.aborted) setState({ kind: 'ready', groups }); })
      .catch((err) => { if (!controller.signal.aborted) setState({ kind: 'error', message: exportErrorMessage(err, 'Could not load the employee groups.') }); });
    return () => controller.abort();
  }, [reloadNonce]);

  useEffect(() => {
    if (!selectedId) return;
    const controller = new AbortController();
    const key = `${selectedId}|${reloadNonce}`;
    accountingGateway.listEmployeeGroupMembers(selectedId, controller.signal)
      .then((list) => { if (!controller.signal.aborted) setKeyedMembers({ key, state: { kind: 'ready', members: list } }); })
      .catch((err) => { if (!controller.signal.aborted) setKeyedMembers({ key, state: isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: exportErrorMessage(err, 'Could not load the members.') } }); });
    return () => controller.abort();
  }, [selectedId, reloadNonce]);
  // A result belongs to the group and reload that asked for it; until its own answer arrives the panel shows loading.
  const members: MembersState = keyedMembers.key === `${selectedId}|${reloadNonce}` ? keyedMembers.state : { kind: 'loading' };

  const groups = state.kind === 'ready' ? state.groups : [];
  const selected = groups.find((g) => g.id === selectedId) ?? null;

  function select(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('groupId', id); else next.delete('groupId');
      return next;
    }, { replace: true });
  }

  async function setActive(group: EmployeeGroup, active: boolean) {
    setBusy(true);
    setDialogError(null);
    try {
      await accountingGateway.setEmployeeGroupActive(group.id, active);
      setDialog(null);
      setNotice(active ? `Group ${group.name} is active again.` : `Group ${group.name} is deactivated. It is kept, and can be reactivated.`);
      setReloadNonce((n) => n + 1);
    } catch (err) {
      setDialogError(exportErrorMessage(err, 'The group could not be changed.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="employee-groups">
      <h4 id="ax-eg-title">Employee groups</h4>
      <p className="muted ax-lead">
        Groups you define to map wages by group (the <strong>Employee group</strong> dimension). An employee must be in exactly one active group on a payroll&apos;s accounting date. Groups are never deleted:
        deactivate one when it is no longer used. Members are shown by employee number.
      </p>
      {notice && <p className="jr-notice jr-notice--ok" role="status" data-testid="groups-notice">{notice}</p>}
      {caps && !caps.canManageMappings && <p className="jr-readonly" role="note">You can view employee groups, but changing them needs permission to manage GL mappings.</p>}
      {state.kind === 'loading' && <p role="status" className="muted">Loading employee groups…</p>}
      {state.kind === 'error' && <RetryableError message={state.message} onRetry={() => { setState({ kind: 'loading' }); setReloadNonce((n) => n + 1); }} />}

      <div className="ax-toolbar">
        <span className="ax-toolbar__spacer" />
        {canManage && <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ kind: 'edit', group: null })} data-testid="new-group">New group</button>}
      </div>

      {state.kind === 'ready' && groups.length === 0 && <p className="muted" data-testid="no-groups-defined">No employee groups yet. Create one, then add its members.</p>}
      {state.kind === 'ready' && groups.length > 0 && (
        <div className="jr-table-scroll" role="region" aria-label="Employee groups table" tabIndex={0}>
          <table className="jr-table jr-table--actions" data-testid="groups-table">
            <caption className="ax-caption">Employee groups ({groups.length})</caption>
            <thead>
              <tr><th scope="col">Name</th><th scope="col">Status</th><th scope="col">Effective</th><th scope="col" className="num">Members</th><th scope="col">Actions</th></tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.id} aria-selected={g.id === selectedId ? true : undefined}>
                  <th scope="row">{g.name}{g.description && <span className="ax-date jr-hint">{g.description}</span>}</th>
                  <td>{g.isActive ? <span className="ax-badge ax-badge--ok"><span aria-hidden="true">✔</span> Active</span> : <span className="ax-badge ax-badge--neutral"><span aria-hidden="true">–</span> Inactive</span>}</td>
                  <td><span className="ax-date">{g.effectiveFrom ? `From ${formatDate(g.effectiveFrom)}` : 'No start date'}</span><span className="ax-date">{g.effectiveTo ? `To ${formatDate(g.effectiveTo)}` : 'No end date'}</span></td>
                  <td className="num">{g.memberCount}</td>
                  <td className="ax-row-actions">
                    <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => select(g.id === selectedId ? '' : g.id)} aria-expanded={g.id === selectedId} aria-label={`${g.id === selectedId ? 'Hide' : 'Show'} members of ${g.name}`}>
                      {g.id === selectedId ? 'Hide members' : 'Members'}
                    </button>
                    {canManage && (
                      <>
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setDialog({ kind: 'edit', group: g })} aria-label={`Edit ${g.name}`}>Edit</button>
                        {g.isActive
                          ? <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => { setDialogError(null); setDialog({ kind: 'deactivate', group: g }); }} aria-label={`Deactivate ${g.name}`}>Deactivate</button>
                          : <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => void setActive(g, true)} disabled={busy} aria-label={`Reactivate ${g.name}`}>Reactivate</button>}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <section className="ax-detail ax-member-list" aria-labelledby="ax-members-title" data-testid="group-members">
          <h4 id="ax-members-title">Members of {selected.name}</h4>
          {members.kind === 'loading' && <p role="status" className="muted">Loading members…</p>}
          {members.kind === 'forbidden' && <p className="jr-readonly" role="note">Viewing members needs permission to view employees, which you do not have.</p>}
          {members.kind === 'error' && <RetryableError message={members.message} onRetry={() => setReloadNonce((n) => n + 1)} />}
          {canManage && selected.isActive && members.kind !== 'forbidden' && (
            <p><button type="button" className="jr-btn jr-btn--primary jr-btn--small" onClick={() => setDialog({ kind: 'add', group: selected })} data-testid="add-members">Add members</button></p>
          )}
          {members.kind === 'ready' && members.members.length === 0 && <p className="muted">This group has no members yet.</p>}
          {members.kind === 'ready' && members.members.length > 0 && (
            <div className="jr-table-scroll" role="region" aria-label="Group members table" tabIndex={0}>
              <table className="jr-table jr-table--actions" data-testid="members-table">
                <caption className="ax-caption">Members by employee number ({members.members.length}); ended memberships are kept as history</caption>
                <thead><tr><th scope="col">Employee</th><th scope="col">From</th><th scope="col">To</th><th scope="col">Status</th>{canManage && <th scope="col">Actions</th>}</tr></thead>
                <tbody>
                  {members.members.map((m) => {
                    const ended = m.effectiveTo !== null && m.effectiveTo < todayIso();
                    return (
                      <tr key={m.membershipId}>
                        <th scope="row">{m.employeeReference}</th>
                        <td>{formatDate(m.effectiveFrom)}</td>
                        <td>{m.effectiveTo ? formatDate(m.effectiveTo) : 'Open'}</td>
                        <td>{ended ? <span className="ax-badge ax-badge--neutral"><span aria-hidden="true">–</span> Ended</span> : <span className="ax-badge ax-badge--ok"><span aria-hidden="true">✔</span> Current</span>}</td>
                        {canManage && (
                          <td>
                            {!ended && (
                              <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setDialog({ kind: 'end', member: m })} aria-label={`End the membership of ${m.employeeReference}`}>End membership</button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {dialog?.kind === 'edit' && (
        <GroupDialog
          group={dialog.group} onClose={() => setDialog(null)}
          onSaved={(g) => { setDialog(null); setNotice(dialog.group ? `Saved ${g.name}.` : `Created the group ${g.name}. Add its members next.`); setReloadNonce((n) => n + 1); if (!dialog.group) select(g.id); }}
        />
      )}
      {dialog?.kind === 'add' && (
        <AddMembersDialog group={dialog.group} onClose={() => setDialog(null)} onSaved={(n) => { setDialog(null); setNotice(`Added ${n} ${n === 1 ? 'member' : 'members'} to ${dialog.group.name}.`); setReloadNonce((x) => x + 1); }} />
      )}
      {dialog?.kind === 'end' && (
        <EndMembershipDialog member={dialog.member} onClose={() => setDialog(null)} onDone={() => { setDialog(null); setNotice(`Ended the membership of ${dialog.member.employeeReference}. It is kept as history.`); setReloadNonce((x) => x + 1); }} />
      )}
      {dialog?.kind === 'deactivate' && (
        <ConfirmDialog title="Deactivate this group?" confirmLabel="Deactivate" onConfirm={() => void setActive(dialog.group, false)} onCancel={() => setDialog(null)} busy={busy} error={dialogError}>
          <p>
            {dialog.group.name} will stop applying to payrolls. Its members and its wage mapping are kept. While it is inactive, a payroll that maps wages by employee group is blocked for its members
            until they are in another active group. Exports already generated do not change.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
